import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Button,
  makeStyles,
  Grid,
} from "@material-ui/core";
import { Select } from "@ofqual/portal-components/components/Select";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { PublicEnquiryModel } from "../../../../domain/PublicEnquiry";
import { assignCase, unassignCase } from "../../../../redux/actions";
import { UserType } from "../../../../redux/types";
import { usersService } from "../../../../services";
import { Error } from "../../../common/Error";

type AssignDialogProps = {
  peModel: PublicEnquiryModel;
  onClose: () => void;
  open: boolean;
  userType: UserType;
  allowReassign: boolean;
};
const useStyles = makeStyles((theme) => ({
  dialogActions: {
    padding: 15,
  },
  body: {
    marginTop: 5,
    marginBottom: 5,
  },
  unassignBtn: {
    marginRight: "auto",
  },
}));

export const AssignDialog: React.FC<AssignDialogProps> = ({
  onClose,
  peModel,
  open,
  userType,
  allowReassign,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { handleSubmit, control, errors } = useForm();

  const [usersLookups] = useLookup({
    service: usersService,
    id: "upn",
    label: (item) => (item.fullName ? item.fullName : item.upn),
    params: {
      userType,
      active: true,
    },
  });

  const handleAssignCase = (data: { assignedToUpn: string }) => {
    const assignedToDisplayName = usersLookups.find(
      (user) => user.id === data.assignedToUpn
    );
    dispatch(
      assignCase(
        peModel.id,
        data.assignedToUpn,
        assignedToDisplayName?.label || data.assignedToUpn
      )
    );
    onClose();
  };

  const handleUnassignCase = () => {
    if (peModel.assignedToUpn != null) {
      dispatch(unassignCase(peModel.id));
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-test="assign-ctr-dialog"
    >
      {allowReassign ? (
        <>
          <DialogTitle data-test="assign-ctr-dialog-title">
            Assign confirmation
            <Divider />
          </DialogTitle>

          <DialogContent>
            <form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="assignedToUpn"
                    defaultValue={null}
                    rules={{ required: true }}
                    as={
                      <Select label="Assign case to" datatest="assign-to">
                        {usersLookups}
                      </Select>
                    }
                  />
                  {errors.assignedToUpn && (
                    <Error data-test="assign-to-error">
                      This field is required
                    </Error>
                  )}
                </Grid>
              </Grid>
            </form>
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button
              className={classes.unassignBtn}
              onClick={handleUnassignCase}
              color="primary"
              data-test="unassign-button"
            >
              Un-assign
            </Button>
            {/* </ExperimentalFeature> */}
            <Button
              onClick={onClose}
              color="primary"
              data-test="close-assign-button"
            >
              Close
            </Button>
            <Button
              onClick={handleSubmit(handleAssignCase)}
              color="primary"
              data-test="confirm-assign-button"
            >
              Confirm
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle data-test="assign-public-enquiry-dialog-title">
            This public enquiry is closed
          </DialogTitle>
          <DialogContent>
            This public enquiry is closed and can no longer be reassigned
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button
              onClick={onClose}
              color="primary"
              data-test="close-assign-button"
            >
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
